import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { cn } from '@/lib/utils'
import { Sparkles } from 'lucide-react'
import { trpc, type RouterOutput } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { HintLabel, InfoTooltip } from './info-tooltip'
import { CATEGORY_META } from './content-categories'

type CategoryRow = RouterOutput['analytics']['contentTypeImpact']['byCategory'][number]
type AggregatedRow = Omit<CategoryRow, 'platform'>

export function ContentTypeImpactCard({ platforms }: { platforms: PlatformType[] }) {
  const { data, isLoading, error } = trpc.analytics.contentTypeImpact.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
  })

  const grouped = data?.byCategory ? groupByPlatform(data.byCategory) : new Map()
  // Content categories are platform-agnostic concepts — when scope is
  // multi-platform, aggregate the per-platform scores into a single ranking.
  // Each post's score was already platform-normalized in the SQL, so a
  // weighted average across platforms is fair.
  const isMultiPlatform = grouped.size > 1
  const aggregated = isMultiPlatform && data?.byCategory ? aggregateImpact(data.byCategory) : null

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Sparkles size={14} className="text-muted-foreground" />
          Content Type Impact
          <InfoTooltip>
            Average engagement by AI-classified content type. Posts are categorized at import from
            their captions; classification is single-primary-tag and stable across runs. Each
            platform compares against its own baseline.
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <SkeletonRows />}

        {error && (
          <p className="text-muted-foreground py-4 text-xs">
            Couldn't load this breakdown. Please refresh.
          </p>
        )}

        {data && !data.hasEnoughData && (
          <p className="text-muted-foreground py-4 text-xs">
            Not enough classified posts yet — content categories are filling in as new posts arrive.
          </p>
        )}

        {data && data.hasEnoughData && aggregated && <Bars rows={aggregated} />}

        {data && data.hasEnoughData && !aggregated && (
          <div className="space-y-3">
            {[...grouped.entries()].map(([platform, rows]) => (
              <div key={platform}>
                <Bars rows={rows} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function Bars({
  rows,
}: {
  rows: {
    category: string
    postCount: number
    avgEngagement: number
    relativeBoost: number | null
    isBaseline: boolean
  }[]
}) {
  const max = Math.max(...rows.map((r) => r.avgEngagement))
  return (
    <div className="space-y-3">
      {rows.map((c) => {
        const meta = CATEGORY_META[c.category]
        const widthPct = max > 0 ? (c.avgEngagement / max) * 100 : 0
        return (
          <div key={c.category}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <HintLabel hint={meta?.hint ?? c.category} className="text-foreground font-medium">
                {meta?.label ?? c.category}
              </HintLabel>
              <span
                className={cn(
                  'font-medium',
                  c.isBaseline
                    ? 'text-muted-foreground'
                    : c.relativeBoost && c.relativeBoost > 0
                      ? 'text-emerald-600 dark:text-emerald-500'
                      : 'text-muted-foreground',
                )}>
                {c.isBaseline ? (
                  <HintLabel hint="Lowest-performing category on this platform.">
                    baseline
                  </HintLabel>
                ) : c.relativeBoost != null ? (
                  `+${(c.relativeBoost * 100).toFixed(0)}%`
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="bg-muted relative h-2 overflow-hidden rounded-full">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${widthPct}%`,
                  backgroundColor: meta?.color ?? 'hsl(var(--primary))',
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i}>
          <div className="bg-muted mb-1 h-3 w-24 animate-pulse rounded" />
          <div className="bg-muted h-2 animate-pulse rounded-full" />
        </div>
      ))}
    </div>
  )
}

function groupByPlatform<T extends { platform: PlatformType }>(rows: T[]): Map<PlatformType, T[]> {
  const m = new Map<PlatformType, T[]>()
  for (const row of rows) {
    const list = m.get(row.platform) ?? []
    list.push(row)
    m.set(row.platform, list)
  }
  return m
}

/** Combine per-(platform, category) rows into a single per-category ranking.
 *  Average scores are weighted by post count so a platform with more posts
 *  in a category contributes more to the aggregate score, then baseline +
 *  relativeBoost are recomputed against the aggregated set. */
function aggregateImpact(rows: CategoryRow[]): AggregatedRow[] {
  const acc = new Map<string, { weighted: number; postCount: number }>()
  for (const r of rows) {
    const a = acc.get(r.category) ?? { weighted: 0, postCount: 0 }
    a.weighted += r.avgEngagement * r.postCount
    a.postCount += r.postCount
    acc.set(r.category, a)
  }
  const collapsed = [...acc.entries()].map(([category, a]) => ({
    category,
    postCount: a.postCount,
    avgEngagement: a.postCount > 0 ? a.weighted / a.postCount : 0,
  }))

  const baseline = Math.min(...collapsed.map((c) => c.avgEngagement))
  return collapsed
    .map((c) => ({
      category: c.category,
      postCount: c.postCount,
      avgEngagement: c.avgEngagement,
      relativeBoost: baseline > 0 ? (c.avgEngagement - baseline) / baseline : null,
      isBaseline: c.avgEngagement === baseline,
    }))
    .sort((a, b) => b.avgEngagement - a.avgEngagement)
}
